import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import api from "@/lib/api";

const INDIAN_STATES = [
  "Andhra Pradesh", "Assam", "Bihar", "Chhattisgarh", "Delhi NCR", "Gujarat", "Haryana",
  "Jharkhand", "Karnataka", "Madhya Pradesh", "Maharashtra", "Odisha", "Punjab",
  "Rajasthan", "Tamil Nadu", "Telangana", "Uttar Pradesh", "West Bengal", "Other",
];

const inputCls =
  "w-full border border-white/10 bg-white/5 px-4 py-3 text-sm text-foreground placeholder:text-sage outline-none transition-colors focus:border-brand-400";

const EnquiryForm = ({ productSlug, role, type = "general", heading = "Send an Enquiry", testId = "enquiry" }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", state: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/enquiries", {
        ...form,
        enquiry_type: type,
        product_slug: productSlug || null,
        role: role || null,
      });
      toast.success(data.message || "Enquiry received. Our team will reach out shortly.");
      setForm({ name: "", email: "", phone: "", state: "", message: "" });
    } catch {
      toast.error("Could not submit your enquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={`${testId}-form`} className="border border-white/10 bg-forest-900 p-8">
      <h3 className="font-display text-2xl font-bold tracking-tight">{heading}</h3>
      {productSlug && (
        <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-brand-400">
          Product enquiry — {productSlug.replace(/-/g, " ")}
        </p>
      )}
      {role && (
        <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-brand-400">
          Applying for — {role}
        </p>
      )}
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <input required value={form.name} onChange={set("name")} placeholder="Full name *" className={inputCls} data-testid={`${testId}-name-input`} />
        <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address *" className={inputCls} data-testid={`${testId}-email-input`} />
        <input value={form.phone} onChange={set("phone")} placeholder="Phone number" className={inputCls} data-testid={`${testId}-phone-input`} />
        <select value={form.state} onChange={set("state")} className={`${inputCls} appearance-none`} data-testid={`${testId}-state-select`}>
          <option value="" className="bg-forest-900">State / Region</option>
          {INDIAN_STATES.map((s) => (
            <option key={s} value={s} className="bg-forest-900">{s}</option>
          ))}
        </select>
      </div>
      <textarea
        value={form.message}
        onChange={set("message")}
        placeholder="Your message"
        rows={4}
        className={`${inputCls} mt-4 resize-none`}
        data-testid={`${testId}-message-input`}
      />
      <button
        type="submit"
        disabled={loading}
        data-testid={`${testId}-submit-button`}
        className="mt-6 flex w-full items-center justify-center gap-2 bg-brand-400 px-6 py-3.5 text-sm font-bold tracking-wide text-forest-950 transition-colors hover:bg-brand-500 disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {loading ? "Submitting…" : "Submit Enquiry"}
      </button>
    </form>
  );
};

export default EnquiryForm;
